import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { FilterFieldConfig } from "./types";
import { useFilterStore } from "./filterStore";
import { getLeafFields, isFieldApplied } from "./fieldUtils";

export interface UseFiltersOptions<TFilters extends Record<string, any>> {
  fields: FilterFieldConfig[];
  /** "url" (default) = query params reais; "none" = useState puro, sem persistência */
  persist?: "url" | "none";
  /**
   * Chave única da lista/página. Quando informada, os filtros aplicados ficam retidos numa store
   * global e são restaurados ao navegar de volta para a tela, mesmo que a URL tenha sido resetada.
   */
  filterKey?: string;
  /**
   * Filtros aplicados quando a tela é aberta sem nenhum filtro na URL e sem nada retido na store.
   * Ficam visíveis na URL e nos controles, então o usuário pode removê-los normalmente — depois de
   * limpos, não voltam sozinhos enquanto a tela estiver montada.
   */
  defaultValues?: Partial<TFilters>;
  /** Validação cross-campo (ex.: período inicial > período final) */
  refine?: (values: Partial<TFilters>) => string | undefined;
}

export interface UseFiltersResult<TFilters extends Record<string, any>> {
  values: Partial<TFilters>;
  appliedCount: number;
  error: string | undefined;
  setValue<K extends keyof TFilters>(key: K, value: TFilters[K] | undefined): void;
  setValues(patch: Partial<TFilters>): void;
  clear(keys?: (keyof TFilters)[]): void;
  /** === `values`, pronto para spread na chamada de serviço */
  queryParams: Partial<TFilters>;
}

function parseValue(field: FilterFieldConfig, raw: string | undefined) {
  if (field.deserialize) return field.deserialize(raw);
  if (raw === undefined) return undefined;
  const result = field.schema.safeParse(raw);
  return result.success ? result.data : undefined;
}

function toRaw(field: FilterFieldConfig, value: unknown): string | undefined {
  if (field.serialize) return field.serialize(value);
  if (value === undefined || value === null || value === "") return undefined;
  return String(value);
}

export function useFilters<TFilters extends Record<string, any>>({
  fields,
  persist = "url",
  filterKey,
  defaultValues,
  refine,
}: UseFiltersOptions<TFilters>): UseFiltersResult<TFilters> {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localValues, setLocalValues] = useState<Partial<TFilters>>(defaultValues ?? {});
  const storedValues = useFilterStore((state) => (filterKey ? state.values[filterKey] : undefined));
  const setStoredValues = useFilterStore((state) => state.setValues);
  const clearStoredValues = useFilterStore((state) => state.clear);

  const leafFields = useMemo(() => getLeafFields(fields), [fields]);

  const rawDefaultValues = useMemo(() => {
    if (!defaultValues) return undefined;

    const raw: Record<string, string> = {};
    for (const field of leafFields) {
      if (!(field.key in defaultValues)) continue;
      const value = toRaw(field, (defaultValues as Record<string, unknown>)[field.key]);
      if (value !== undefined) raw[field.key] = value;
    }
    return Object.keys(raw).length ? raw : undefined;
  }, [leafFields]);

  const seedValues = (filterKey ? storedValues : undefined) ?? rawDefaultValues;

  const hasSeededRef = useRef(false);

  useEffect(() => {
    hasSeededRef.current = true;

    if (persist !== "url" || !seedValues) return;
    if (leafFields.some((field) => searchParams.has(field.key))) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(seedValues).forEach(([key, raw]) => next.set(key, raw));
        return next;
      },
      { replace: true },
    );
  }, [filterKey]);

  const values = useMemo(() => {
    if (persist === "none") return localValues;

    const source = new URLSearchParams(searchParams);
    const hasUrlFilters = leafFields.some((field) => searchParams.has(field.key));

    if (!hasUrlFilters && !hasSeededRef.current && seedValues) {
      Object.entries(seedValues).forEach(([key, raw]) => source.set(key, raw));
    }

    const result: Partial<TFilters> = {};
    for (const field of leafFields) {
      const raw = source.get(field.key) ?? undefined;
      const parsed = parseValue(field, raw);
      if (parsed !== undefined) (result as Record<string, unknown>)[field.key] = parsed;
    }
    return result;
  }, [persist, localValues, searchParams, leafFields, seedValues]);

  const applyPatch = (patch: Partial<TFilters>) => {
    if (persist === "none") {
      setLocalValues((prev) => ({ ...prev, ...patch }));
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const field of leafFields) {
          if (!(field.key in patch)) continue;
          const raw = toRaw(field, (patch as Record<string, unknown>)[field.key]);
          if (raw === undefined) next.delete(field.key);
          else next.set(field.key, raw);
        }

        if (filterKey) {
          const raw: Record<string, string> = {};
          for (const field of leafFields) {
            const value = next.get(field.key);
            if (value !== null) raw[field.key] = value;
          }
          setStoredValues(filterKey, raw);
        }

        return next;
      },
      { replace: true },
    );
  };

  const setValue: UseFiltersResult<TFilters>["setValue"] = (key, value) => {
    applyPatch({ [key]: value } as Partial<TFilters>);
  };

  const setValues: UseFiltersResult<TFilters>["setValues"] = (patch) => {
    applyPatch(patch);
  };

  const clear: UseFiltersResult<TFilters>["clear"] = (keys) => {
    const targetFields = keys ? leafFields.filter((field) => keys.includes(field.key as keyof TFilters)) : leafFields;
    const patch = targetFields.reduce((acc, field) => ({ ...acc, [field.key]: undefined }), {} as Partial<TFilters>);
    applyPatch(patch);
    if (!keys && filterKey) clearStoredValues(filterKey);
  };

  const appliedCount = fields.filter((field) => isFieldApplied(field, values)).length;
  const error = refine?.(values);

  return { values, appliedCount, error, setValue, setValues, clear, queryParams: values };
}
